// Copyright (c) StrangeBee 2024

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { useLocalMessage } from '@Helpers/locale';
import useValidateForm from '@Hooks/useValidateForm';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import React, { ReactElement, useMemo } from 'react';
import { CustomFieldOutput } from 'thehive-sdk';

interface CustomFieldGroupValues {
    group: string;
}

interface CustomFieldGroupEditProps {
    customFieldList?: CustomFieldOutput[];
    triggerElem: ReactElement;
    onFinish: (group: string) => void;
}

export const CustomFieldGroupEdit = ({
    customFieldList = [],
    onFinish,
    triggerElem,
}: CustomFieldGroupEditProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const groups = useMemo(
        () => Array.from(new Set(customFieldList?.map(({ group = '' }) => group))) || [],
        [customFieldList],
    );

    const onFinishHandler = ({ group }: CustomFieldGroupValues) => {
        onFinish(group);
    };

    return (
        <THDrawer
            title={i('customField.group.drawer.title')}
            triggerElem={triggerElem}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <THForm<CustomFieldGroupValues>
                    form={form}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onFinish={(values) => {
                        onFinishHandler(values);
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                onCancel={() => {
                                    setFieldsTouched(false);
                                    form.resetFields();
                                    toggleDrawer();
                                }}
                                disabled={!isValid || !fieldsTouched}
                            />
                        }
                    >
                        <THForm.Item
                            name="group"
                            label={i('customField.group.form.group.label')}
                            rules={[{ required: true }]}
                        >
                            <THAutoComplete
                                options={groups.map((value) => ({ label: value, value }))}
                                filtered
                                placeholder={i('customField.group.form.group.placeholder')}
                            />
                        </THForm.Item>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
